import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RecoverySurveyModal } from '@/components/recovery/RecoverySurveyModal';
import ReactMarkdown from 'react-markdown';
import { JourneyTimeline } from '@/components/learning/JourneyTimeline';
import { ReflectionSection } from '@/components/learning/ReflectionSection';
import { AssessmentSection } from '@/components/learning/AssessmentSection';
import remarkGfm from 'remark-gfm';
import Header from '@/components/layout/Header';
import { SanitizedHtml } from '@/components/SanitizedHtml';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  BookOpen,
  CheckCircle2,
  Play,
  Target,
  ChevronDown,
  ChevronUp,
  Pause,
  Trophy,
  Eye,
  X,
  Sparkles,
  Lightbulb,
  MessageCircle,
  Calendar,
  TrendingUp,
  MapPin,
  Zap,
  Circle,
  AlertCircle,
  User,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import { ProfileAvatar } from '@/components/profiles/ProfileAvatar';
import { useProfilePictureUrl } from '@/hooks/useProfilePictureUrl';
import { COACH_START_LESSON_KEY } from '@/pages/coach/CoachContext';
import { ACCESS_TOKEN_KEY } from '@/constants/storageKeys';
import {
  userLearningPlanApi,
  userLessonApi,
  learningPlanApi,
  lessonApi,
  userTipApi,
  userNudgeApi,
  userTidbitApi,
  userHackApi,
  userNanoApi,
  tipApi,
  nudgeApi,
  tidbitApi,
  hackApi,
  nanoApi,
} from '@/services/learningManagementApi';
import { profileApi } from '@/services/profileApi';
import { useAuth } from '@/contexts/AuthContext';
import { painPointApi, winApi, skillApi } from '@/services/skillKnowledgeApi';
import { stripLearningJourneyPrefix, isGenericLabel } from '@/utils/learningPathDisplayUtils';
import type {
  ActivePlanDashboardResponse,
  LearningPlan,
  Lesson,
  UserLessonResponse,
  UserLessonStatus,
  Tip,
  Nudge,
  Tidbit,
  Hack,
  Nano,
} from '@/types/learningManagement';
import type { UserProfile } from '@/types/userProfile';
import { EmailVerificationBanner } from '@/components/common/EmailVerificationBanner';
import { SubscriptionGuard } from '@/components/common/SubscriptionGuard';

/** Same key as coach/onboarding: logged-in user for local dev; later will use token. */
const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

function getEmailFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.signUpEmail ?? parsed.email ?? null;
  } catch {
    return null;
  }
}

/** Persist user to localStorage so subsequent loads work without URL param. */
function setUserInStorage(email: string): void {
  try {
    localStorage.setItem(LOCAL_USER_INFO_KEY, JSON.stringify({ signUpEmail: email, email }));
  } catch {
    // ignore
  }
}

/** Read user from URL query (?user= or ?email=) or path. Uses window.location for reliability. */
function getEmailFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('user') ?? params.get('email');
  if (fromQuery && fromQuery.includes('@')) return fromQuery;
  const pathMatch = window.location.pathname.match(/\/learning-plan\/([^/?#]+)/);
  const fromPath = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
  return fromPath && fromPath.includes('@') ? fromPath : null;
}

/** Extract `exp` claim (ms) from JWT payload. Returns 0 on failure/unparseable. */
function getTokenExpMs(token: string): number {
  try {
    const base64Url = token.split('.')[1] || '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = atob(padded);
    const expSeconds = JSON.parse(jsonPayload).exp;
    return typeof expSeconds === 'number' ? expSeconds * 1000 : 0;
  } catch {
    return 0;
  }
}

const STATUS_CONFIG: Record<
  UserLessonStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }
> = {
  DRAFT: { label: 'Draft', variant: 'secondary', icon: <BookOpen className="w-3 h-3" /> },
  PLANNED: { label: 'Planned', variant: 'outline', icon: <Target className="w-3 h-3" /> },
  ACTIVE: { label: 'In progress', variant: 'default', icon: <Play className="w-3 h-3" /> },
  PAUSED: { label: 'Paused', variant: 'secondary', icon: <Pause className="w-3 h-3" /> },
  COMPLETED: { label: 'Completed', variant: 'default', icon: <CheckCircle2 className="w-3 h-3" /> },
  EXPIRED: { label: 'Expired', variant: 'destructive', icon: <X className="w-3 h-3" /> },
  ARCHIVED: { label: 'Archived', variant: 'secondary', icon: <X className="w-3 h-3" /> },
};

/** Get displayable body text from content entities (Tip, Nudge, Tidbit, Hack, Nano). Supports both camelCase and snake_case from API. */
function getContentBody(item: Tip | Nudge | Tidbit | Hack | Nano): string {
  const any = item as unknown as Record<string, unknown>;
  const content =
    any.content ?? any.description ?? any.summary ?? any.preview ??
    any.content_description ?? any.problem_statement ?? any.solution ?? '';
  if (typeof content === 'string' && content.trim()) return content.trim();
  const hack = item as Hack;
  if (hack.solution?.trim()) return hack.solution.trim();
  if (Array.isArray(hack.implementationSteps) && hack.implementationSteps.length > 0) {
    return hack.implementationSteps.map((step, i) => `${i + 1}. ${step}`).join('\n\n');
  }
  return '';
}

/** True if item has a real title or body — don't show "Untitled / No content yet" cards. */
function hasMeaningfulContent(item: Tip | Nudge | Tidbit | Hack | Nano): boolean {
  const title = (item as { title?: string }).title?.trim();
  if (title && title.toLowerCase() !== 'untitled') return true;
  return getContentBody(item).length > 0;
}

/** Mini card for sidebar "Today's content": title, short preview, Coach CTA. */
function TodayContentMiniCard({
  title,
  body,
  variant,
  onOpenCoach,
}: {
  title: string;
  body: string;
  variant: 'tip' | 'nudge' | 'tidbit' | 'hack' | 'nano';
  onOpenCoach: () => void;
}) {
  const preview = body ? (body.length > 100 ? `${body.slice(0, 100).trim()}…` : body) : '';
  const variantStyles = {
    tip: 'border-l-4 border-l-amber-500 bg-amber-500/5 dark:bg-amber-500/10',
    nudge: 'border-l-4 border-l-primary bg-primary/5 dark:bg-primary/10',
    tidbit: 'border-l-4 border-l-violet-500 bg-violet-500/5 dark:bg-violet-500/10',
    hack: 'border-l-4 border-l-emerald-500 bg-emerald-500/5 dark:bg-emerald-500/10',
    nano: 'border-l-4 border-l-sky-500 bg-sky-500/5 dark:bg-sky-500/10',
  };
  const icons = {
    tip: <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />,
    nudge: <MessageCircle className="w-4 h-4 text-primary shrink-0" />,
    tidbit: <Sparkles className="w-4 h-4 text-violet-500 shrink-0" />,
    hack: <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />,
    nano: <Target className="w-4 h-4 text-sky-600 dark:text-sky-400 shrink-0" />,
  };
  return (
    <div className={`rounded-xl p-3 ${variantStyles[variant]} transition-shadow hover:shadow-md`}>
      <div className="flex gap-2">
        {icons[variant]}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground text-sm leading-tight">{title}</p>
          {preview && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">{preview}</p>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-7 px-2 text-xs rounded-lg gap-1"
            onClick={onOpenCoach}
          >
            <MessageCircle className="w-3 h-3" />
            Discuss in Coach
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Reusable content card for Tip/Nudge/Tidbit/Hack/Nano: title, markdown body, Coach CTA. */
function DayContentCard({
  item,
  variant,
  onOpenCoach,
}: {
  item: Tip | Nudge | Tidbit | Hack | Nano;
  variant: 'tip' | 'nudge' | 'tidbit' | 'hack' | 'nano';
  onOpenCoach: (prompt?: string) => void;
}) {
  const body = getContentBody(item);
  const title = (item as { title?: string }).title ?? 'Untitled';
  const variantStyles = {
    tip: 'bg-amber-500/10 dark:bg-amber-500/15 border-amber-500/20',
    nudge: 'bg-primary/8 dark:bg-primary/12 border-primary/20',
    tidbit: 'bg-violet-500/10 dark:bg-violet-500/15 border-violet-500/20',
    hack: 'bg-emerald-500/10 dark:bg-emerald-500/15 border-emerald-500/20',
    nano: 'bg-sky-500/10 dark:bg-sky-500/15 border-sky-500/20',
  };
  const icons = {
    tip: <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />,
    nudge: <MessageCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />,
    tidbit: <Sparkles className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />,
    hack: <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />,
    nano: <Target className="w-4 h-4 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />,
  };
  return (
    <div className={`rounded-xl border p-4 ${variantStyles[variant]} transition-shadow hover:shadow-md`}>
      <div className="flex gap-3">
        {icons[variant]}
        <div className="min-w-0 flex-1">
          <h5 className="font-semibold text-foreground">{title}</h5>
          {body ? (
            <div className="mt-2 prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-p:text-muted-foreground prose-p:leading-relaxed prose-ul:my-2 prose-li:my-0">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-1 italic">No content yet.</p>
          )}
          <Button
            variant="outline"
            size="sm"
            className="mt-3 rounded-lg gap-1.5"
            onClick={() => onOpenCoach(`Tell me more about: ${title}`)}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Discuss in Coach
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Reusable lesson card with View/Start/Complete and Coach link — same UI as Today's focus */
function LessonCard({
  lesson,
  actionLoading,
  onView,
  onStart,
  onComplete,
  onStartInCoach,
  coachLabel,
}: {
  lesson: UserLessonResponse;
  actionLoading: string | null;
  onView: (l: UserLessonResponse) => void;
  onStart: (l: UserLessonResponse) => void;
  onComplete: (l: UserLessonResponse) => void;
  onStartInCoach: () => void;
  coachLabel?: string;
}) {
  const cfg = STATUS_CONFIG[lesson.status] ?? STATUS_CONFIG.PLANNED;
  const canStart = lesson.status === 'DRAFT' || lesson.status === 'PLANNED';
  const isCompleted = lesson.status === 'COMPLETED';
  return (
    <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-border bg-background/80 hover:bg-background transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h5 className="font-semibold text-foreground">{lesson.title}</h5>
          <Badge variant={cfg.variant} className="gap-1 rounded-md">
            {cfg.icon}
            {cfg.label}
          </Badge>
        </div>
        {lesson.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{lesson.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(lesson)}
          disabled={actionLoading === lesson.id}
          className="rounded-lg"
        >
          {actionLoading === lesson.id ? <BrandedSpinner sizeClassName="h-4 w-4" /> : <><Eye className="w-4 h-4 mr-1" />View</>}
        </Button>
        {canStart && (
          <Button size="sm" onClick={() => onStart(lesson)} disabled={actionLoading === lesson.id} className="rounded-lg">
            {actionLoading === lesson.id ? <BrandedSpinner sizeClassName="h-4 w-4" /> : <><Play className="w-4 h-4 mr-1" />Start</>}
          </Button>
        )}
        {lesson.status === 'ACTIVE' && (
          <Button size="sm" onClick={() => onComplete(lesson)} disabled={actionLoading === lesson.id} className="rounded-lg">
            {actionLoading === lesson.id ? <BrandedSpinner sizeClassName="h-4 w-4" /> : <><CheckCircle2 className="w-4 h-4 mr-1" />Complete</>}
          </Button>
        )}
        {!isCompleted && lesson.status !== 'ACTIVE' && !canStart && (
          <Button size="sm" variant="secondary" onClick={() => onComplete(lesson)} disabled={actionLoading === lesson.id} className="rounded-lg">
            <CheckCircle2 className="w-4 h-4 mr-1" />Mark complete
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onStartInCoach} className="rounded-lg gap-1.5">
          <MessageCircle className="w-4 h-4" />
          {coachLabel ?? 'Coach'}
        </Button>
      </div>
    </li>
  );
}

type DayContent = {
  dayNumber: number;
  title?: string;
  description?: string;
  learningObjectives?: string[];
  [key: string]: unknown;
};

/** Cumulative completed count up to and including day d (for chart curve). */
function completedCountUpToDay(d: number, completedDayNumbers: number[]): number {
  return completedDayNumbers.filter((n) => n <= d).length;
}

/** Interactive drill-down progress line chart — uses backend completedDayNumbers when provided. */
function ProgressLineChart({
  totalDays,
  daysCompleted,
  completedDayNumbers,
  currentDay,
  selectedDay,
  onDayClick,
}: {
  totalDays: number;
  daysCompleted: number;
  /** When set, dots and curve use actual completed days (backend tracking). */
  completedDayNumbers?: number[];
  currentDay: number;
  selectedDay: number | null;
  onDayClick: (day: number) => void;
}) {
  const width = 320;
  const height = 140;
  const padding = { top: 16, right: 16, bottom: 36, left: 40 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const maxDays = Math.max(totalDays, 1);
  const completedSet = completedDayNumbers && completedDayNumbers.length > 0
    ? new Set(completedDayNumbers)
    : null;

  const points: { day: number; x: number; y: number; completed: number }[] = [];
  for (let d = 0; d <= maxDays; d++) {
    const completed = completedSet
      ? completedCountUpToDay(d, completedDayNumbers!)
      : Math.min(d, daysCompleted);
    const yVal = maxDays > 0 ? (completed / maxDays) * innerHeight : 0;
    points.push({
      day: d,
      x: padding.left + (d / maxDays) * innerWidth,
      y: padding.top + innerHeight - yVal,
      completed,
    });
  }

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  const areaD = `${pathD} L ${points[points.length - 1]?.x ?? 0} ${padding.top + innerHeight} L ${points[0]?.x ?? 0} ${padding.top + innerHeight} Z`;

  return (
    <div className="w-full">
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="lpChartGradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.15" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="lpChartLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="hsl(263 70% 50%)" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#lpChartGradient)" />
        <path
          d={pathD}
          fill="none"
          stroke="url(#lpChartLine)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points
          .filter((p) => p.day > 0)
          .map((p) => {
            const isSelected = selectedDay === p.day;
            const isCurrent = currentDay === p.day;
            const isComplete = completedSet ? completedSet.has(p.day) : p.day <= daysCompleted;
            return (
              <g key={p.day}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={isSelected ? 8 : isCurrent ? 6 : 4}
                  fill={isComplete ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground) / 0.4)'}
                  stroke={isSelected ? 'hsl(var(--foreground))' : 'transparent'}
                  strokeWidth={2}
                  className="cursor-pointer transition-all hover:opacity-90"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onDayClick(p.day)}
                  aria-label={`Day ${p.day}${isComplete ? ', completed' : ''}`}
                />
              </g>
            );
          })}
        {/* X-axis labels */}
        {[1, Math.ceil(maxDays / 2), maxDays].filter((d, i, arr) => arr.indexOf(d) === i).map((d) => {
          const px = padding.left + (d / maxDays) * innerWidth;
          return (
            <text
              key={d}
              x={px}
              y={height - 10}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px] font-medium"
            >
              Day {d}
            </text>
          );
        })}
      </svg>
      <p className="text-center text-xs text-muted-foreground mt-1">
        Click a point to jump to that day
      </p>
    </div>
  );
}

export const LearningPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, isLoading: authLoading, accessToken } = useAuth();
  const { userEmail: pathUserEmail } = useParams<{ userEmail?: string }>();
  const [searchParams] = useSearchParams();
  const urlUserFromRouter = searchParams.get('user') ?? searchParams.get('email') ?? pathUserEmail;
  const urlUser = urlUserFromRouter ?? getEmailFromUrl();
  const [email, setEmail] = useState<string | null>(() => {
    const fromStorage = getEmailFromStorage();
    if (fromStorage) return fromStorage;
    const fromUrl = getEmailFromUrl();
    if (fromUrl) return fromUrl;
    const candidate = urlUserFromRouter ?? pathUserEmail;
    if (candidate && typeof candidate === 'string' && candidate.includes('@')) return candidate;
    return null;
  });
  const [dashboard, setDashboard] = useState<ActivePlanDashboardResponse | null>(null);
  const [learningPlanFromApi, setLearningPlanFromApi] = useState<LearningPlan | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Must call hooks before any early return — React rules of hooks
  const { url: profilePictureUrl } = useProfilePictureUrl(profile?.profilePictureId);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lpRecoveryModalOpen, setLpRecoveryModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewingLesson, setViewingLesson] = useState<UserLessonResponse | null>(null);
  const [fullLesson, setFullLesson] = useState<Lesson | null>(null);
  const [openAccordionDay, setOpenAccordionDay] = useState<number | null>(null);
  const [chartSelectedDay, setChartSelectedDay] = useState<number | null>(null);

  // Post-completion flow state
  const [reflectionDone, setReflectionDone] = useState(false);
  const [assessmentDone, setAssessmentDone] = useState(false);
  const [startingNextPlan, setStartingNextPlan] = useState(false);
  const [lessonsByDay, setLessonsByDay] = useState<Record<number, UserLessonResponse[]>>({});
  const [lessonsLoadingByDay, setLessonsLoadingByDay] = useState<Record<number, boolean>>({});
  const [dayExtraContent, setDayExtraContent] = useState<
    Record<number, { tips: Tip[]; nudges: Nudge[]; tidbits: Tidbit[]; hacks: Hack[]; nanos: Nano[] }>
  >({});
  const [dayExtraContentLoading, setDayExtraContentLoading] = useState<Record<number, boolean>>({});
  const [journeyContent, setJourneyContent] = useState<{ wins: string[]; painPoints: string[]; skills: string[] }>({ wins: [], painPoints: [], skills: [] });
  const [primaryWinDetail, setPrimaryWinDetail] = useState<{ name: string; description?: string } | null>(null);
  // Personalized nudges from learning-goal-service (independent of daily content IDs)
  const [personalizedNudges, setPersonalizedNudges] = useState<Record<string, unknown>[]>([]);

  const [todayContentItems, setTodayContentItems] = useState<{
    tips: Tip[];
    nudges: Nudge[];
    tidbits: Tidbit[];
    hacks: Hack[];
    nanos: Nano[];
  }>({ tips: [], nudges: [], tidbits: [], hacks: [], nanos: [] });
  const [todayContentLoading, setTodayContentLoading] = useState(false);
  const pathTimelineRef = React.useRef<HTMLDivElement>(null);

  // When signed out, redirect to login so we don't show another user's data from storage
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      // If a user refreshes while AuthContext is still populating `user`,
      // `isAuthenticated` may briefly be false even though we have a valid access token
      // or refresh token. In that case, avoid redirecting to `/login` to prevent loops.
      try {
        const access = localStorage.getItem(ACCESS_TOKEN_KEY);
        const refresh = localStorage.getItem('five_ai_refresh_token');
        if (access || refresh) {
          // Token exists in localStorage — wait for AuthContext to finish hydrating
          // (it will refresh the token if expired). Only redirect after a timeout.
          const expMs = access ? getTokenExpMs(access) : 0;
          const now = Date.now();
          if (expMs === 0 || expMs > now || refresh) return;
        }
      } catch {
        // If storage is unavailable, fall back to redirect.
      }
      navigate('/login?next=/learning-plan', { replace: true });
      return;
    }
  }, [authLoading, isAuthenticated, navigate]);

  // When authenticated, use auth user's email so we show the correct user (not stale storage)
  useEffect(() => {
    if (!isAuthenticated || !authUser?.email) return;
    setUserInStorage(authUser.email);
    setEmail(authUser.email);
  }, [isAuthenticated, authUser?.email]);

  useEffect(() => {
    if (isAuthenticated) return;
    const fromStorage = getEmailFromStorage();
    if (fromStorage) {
      setEmail(fromStorage);
      return;
    }
    const fromUrl = getEmailFromUrl();
    const resolved = fromUrl ?? (urlUser && typeof urlUser === 'string' && urlUser.includes('@') ? urlUser : null);
    if (resolved) {
      setUserInStorage(resolved);
      setEmail(resolved);
    } else {
      setEmail(null);
    }
  }, [urlUser, pathUserEmail, isAuthenticated]);

  // Persist URL user to storage when used (so reload without param works)
  useEffect(() => {
    if (email && urlUser && email === urlUser) {
      setUserInStorage(email);
    }
  }, [email, urlUser]);

  const fetchData = useCallback(async () => {
    if (!email) return;
    try {
      setError(null);
      // Coach uses BOTH: getByUser (learning-paths) and getDashboard (user-learning-plans).
      // Dashboard may not include activePlan; learning-paths returns the full plan.
      const [dashboardRes, planRes] = await Promise.allSettled([
        userLearningPlanApi.getDashboard(email),
        learningPlanApi.getByUser(email),
      ]);
      const dashboardData = dashboardRes.status === 'fulfilled' ? dashboardRes.value : null;
      const planData = planRes.status === 'fulfilled' ? planRes.value : null;
      if (import.meta.env.DEV) {
        const planModules = (planData as { learningModules?: unknown[] } | null)?.learningModules ?? [];
        const firstMod = planModules[0] as { dailyContents?: unknown[] } | undefined;
        const firstModContentsCount = firstMod?.dailyContents?.length ?? 0;
        console.log('[LearningPlan] API responses:', {
          dashboardStatus: dashboardRes.status,
          planStatus: planRes.status,
          todaysLessonsCount: dashboardData?.todaysLessons?.length ?? 0,
          activePlanId: dashboardData?.activePlan?.id,
          planId: planData?.id,
          planModulesCount: planModules.length,
          firstModuleDailyContentsCount: firstModContentsCount,
          planDataShape: planData != null ? { keys: Object.keys(planData as object), hasLearningModules: 'learningModules' in (planData as object) } : null,
        });
      }
      setDashboard(dashboardData ?? null);
      setLearningPlanFromApi(planData ?? null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load your learning plan'));
      setDashboard(null);
      setLearningPlanFromApi(null);
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  useEffect(() => {
    // Important: wait for AuthContext bootstrap so token is injected into API services.
    if (authLoading) return;
    // If we still don't have an access token (and auth user isn't set), let the redirect effect handle it.
    if (!isAuthenticated && !accessToken) return;
    if (email) {
      setIsLoading(true);
      fetchData();
      const loadProfile = email.includes('@')
        ? profileApi.getByEmail(email)
        : profileApi.getById(email);
      loadProfile.then(setProfile).catch(() => setProfile(null));
    } else {
      setIsLoading(false);
    }
  }, [email, fetchData, authLoading, accessToken, isAuthenticated]);

  const handleLessonStart = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    try {
      const updated = await userLessonApi.start(lesson.id);
      setViewingLesson(updated);
      await fetchData();
    } finally {
      setActionLoading(null);
    }
  };

  const handleLessonView = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    setFullLesson(null);
    try {
      // Fetch full user lesson detail (GET returns content; list/view may not)
      const fullUserLesson = await userLessonApi.getById(lesson.id);
      setViewingLesson(fullUserLesson);
      await userLessonApi.view(lesson.id).catch(() => {}); // Fire-and-forget for tracking
      await fetchData();
      // Fetch lesson template for headline + followupPrompts (uses lessonId, not user-lesson id)
      const lessonTemplateId = (lesson.lessonId ?? fullUserLesson.lessonId ?? lesson.id) as string;
      if (lessonTemplateId) {
        try {
          const full = await lessonApi.getById(lessonTemplateId);
          setFullLesson(full);
        } catch {
          setFullLesson(null);
        }
      }
    } catch {
      setViewingLesson(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLessonComplete = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    try {
      setError(null);
      await userLessonApi.complete(lesson.id);
      await fetchData();
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to complete lesson'));
      // Force the error card to show (it only renders when error && !dashboard).
      setDashboard(null);
      setLearningPlanFromApi(null);
    } finally {
      setActionLoading(null);
    }
  };

  // Use plan for planId and "no plan" check (dashboard.activePlan may be minimal)
  const plan = dashboard?.activePlan ?? learningPlanFromApi ?? null;
  // Day list: always prefer learning-paths (getByUser) when available — same source as Coach context panel
  const planForDayList = learningPlanFromApi ?? plan;
  const todaysContent = dashboard?.todaysContent;
  const todaysLessons = dashboard?.todaysLessons ?? [];
  const currentDay = dashboard?.currentDayNumber ?? 1;
  const totalDays = dashboard?.totalDays ?? 7;
  const daysCompleted = dashboard?.daysCompleted ?? 0;
  // Prefer backend overallProgress; derive from daysCompleted/totalDays when backend sends 0 or omits it
  const progress =
    dashboard?.overallProgress != null && Number(dashboard.overallProgress) > 0
      ? Number(dashboard.overallProgress)
      : totalDays > 0
        ? (daysCompleted / totalDays) * 100
        : 0;
  const daysRemaining = dashboard?.daysRemaining ?? 6;
  const completedDayNumbers = dashboard?.completedDayNumbers ?? [];
  const overdueDayNumbers = dashboard?.overdueDayNumbers ?? [];

  const planRaw = planForDayList as Record<string, unknown> | null | undefined;
  // Prefer active plan's modules so we show only the current plan's days (e.g. 7), not all modules (e.g. 14 when path has 2 plans)
  const modules = useMemo(() => {
    const fromActive = dashboard?.activePlan?.learningModules;
    if (Array.isArray(fromActive) && fromActive.length > 0) return fromActive;
    const fromPlan = planForDayList?.learningModules ?? planRaw?.learning_modules;
    return Array.isArray(fromPlan) ? fromPlan : [];
  }, [dashboard?.activePlan?.learningModules, planForDayList, planRaw]);
  // Flatten dailyContents from all modules so all 7 days from API are shown
  const dailyContents = useMemo(() => {
    const out: unknown[] = [];
    let dayIndex = 1;
    for (const mod of modules) {
      const m = mod as Record<string, unknown>;
      const contents = m?.dailyContents ?? m?.daily_contents ?? m?.dailyContent ?? m?.goals;
      const arr = Array.isArray(contents) ? contents : [];
      for (const d of arr) {
        const raw = d as Record<string, unknown>;
        out.push({ ...raw, dayNumber: raw.dayNumber ?? raw.day_number ?? raw.day ?? dayIndex });
        dayIndex += 1;
      }
    }
    return out;
  }, [modules]);
  const allDays: DayContent[] = useMemo(() =>
    dailyContents.map((d, i) => {
      const raw = d as Record<string, unknown>;
      const dayNum = (raw.dayNumber ?? raw.day_number ?? raw.day ?? i + 1) as number;
      const title = raw.title ?? raw.lesson_title ?? raw.lessonTitle ?? raw.name;
      return { ...raw, dayNumber: dayNum, title: typeof title === 'string' ? title : raw.title } as DayContent;
    }),
    [dailyContents]
  );
  // Ensure we show all days returned by API (e.g. 7), not just dashboard.totalDays
  const displayTotalDays = Math.max(totalDays, allDays.length) || 7;
  // Today's day content for sidebar counts (tips, nudges, etc.)
  const todayDayRaw = useMemo(
    () => allDays.find((d) => d.dayNumber === currentDay),
    [allDays, currentDay]
  );
  const ids = (raw: DayContent | undefined, key: string, keySnake: string): number => {
    if (!raw) return 0;
    const a = raw[key] ?? raw[keySnake];
    return Array.isArray(a) ? a.length : 0;
  };
  const todayTipCount = ids(todayDayRaw, 'tipIds', 'tip_ids');
  const todayNudgeCount = ids(todayDayRaw, 'nudgeIds', 'nudge_ids');
  const todayTidbitCount = ids(todayDayRaw, 'tidbitIds', 'tidbit_ids');
  const todayHackCount = ids(todayDayRaw, 'hackIds', 'hack_ids');
  const todayNanoCount = ids(todayDayRaw, 'nanoIds', 'nano_ids');

  const getIdList = (raw: DayContent | undefined, key: string, keySnake: string): string[] => {
    if (!raw) return [];
    const a = raw[key] ?? raw[keySnake];
    return Array.isArray(a) ? a.filter((id): id is string => typeof id === 'string' && id.length > 0) : [];
  };

  // Fetch personalized nudges from learning-goal-service (independent of daily content)
  useEffect(() => {
    if (!email) return;
    userNudgeApi.getByUser(email).then((nudges) => {
      const active = nudges.filter((n) => {
        const st = ((n.status ?? '') as string).toUpperCase();
        return st === 'PLANNED' || st === 'ACTIVE' || st === 'PUBLISHED';
      });
      setPersonalizedNudges(active.length > 0 ? active.slice(0, 4) : nudges.slice(0, 4));
    }).catch(() => { /* non-critical */ });
  }, [email]);

  // Fetch today's tip/nudge/tidbit/hack/nano full content via template APIs (same as admin pages) for sidebar display
  useEffect(() => {
    if (!todayDayRaw) return;
    const tipIdList = getIdList(todayDayRaw, 'tipIds', 'tip_ids');
    const nudgeIdList = getIdList(todayDayRaw, 'nudgeIds', 'nudge_ids');
    const tidbitIdList = getIdList(todayDayRaw, 'tidbitIds', 'tidbit_ids');
    const hackIdList = getIdList(todayDayRaw, 'hackIds', 'hack_ids');
    const nanoIdList = getIdList(todayDayRaw, 'nanoIds', 'nano_ids');
    if (
      tipIdList.length === 0 &&
      nudgeIdList.length === 0 &&
      tidbitIdList.length === 0 &&
      hackIdList.length === 0 &&
      nanoIdList.length === 0
    ) {
      setTodayContentItems({ tips: [], nudges: [], tidbits: [], hacks: [], nanos: [] });
      return;
    }
    let cancelled = false;
    setTodayContentLoading(true);
    const fetchOne = async (
      id: string,
      templateFetch: (id: string) => Promise<unknown>,
      userFetch?: (id: string) => Promise<unknown>
    ): Promise<unknown> => {
      try {
        return await templateFetch(id);
      } catch {
        if (userFetch) {
          try {
            return await userFetch(id);
          } catch {
            return null;
          }
        }
        return null;
      }
    };
    Promise.all([
      Promise.allSettled(tipIdList.map((id) => fetchOne(id, tipApi.getById, userTipApi.getById))).then((r) =>
        r.map((x) => (x.status === 'fulfilled' && x.value ? x.value : null)).filter((t): t is Tip => t != null)
      ),
      Promise.allSettled(nudgeIdList.map((id) => fetchOne(id, nudgeApi.getById, userNudgeApi.getById))).then((r) =>
        r.map((x) => (x.status === 'fulfilled' && x.value ? x.value : null)).filter((n): n is Nudge => n != null)
      ),
      Promise.allSettled(tidbitIdList.map((id) => fetchOne(id, tidbitApi.getById, userTidbitApi.getById))).then((r) =>
        r.map((x) => (x.status === 'fulfilled' && x.value ? x.value : null)).filter((t): t is Tidbit => t != null)
      ),
      Promise.allSettled(hackIdList.map((id) => fetchOne(id, hackApi.getById, userHackApi.getById))).then((r) =>
        r.map((x) => (x.status === 'fulfilled' && x.value ? x.value : null)).filter((h): h is Hack => h != null)
      ),
      Promise.allSettled(nanoIdList.map((id) => fetchOne(id, nanoApi.getById, userNanoApi.getById))).then((r) =>
        r.map((x) => (x.status === 'fulfilled' && x.value ? x.value : null)).filter((n): n is Nano => n != null)
      ),
    ]).then(([tips, nudges, tidbits, hacks, nanos]) => {
      if (!cancelled) {
        setTodayContentItems({ tips, nudges, tidbits, hacks, nanos });
      }
    }).finally(() => {
      if (!cancelled) setTodayContentLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [todayDayRaw]);

  // Scroll to day when chart day is selected
  useEffect(() => {
    if (chartSelectedDay && pathTimelineRef.current) {
      const el = document.getElementById(`day-${chartSelectedDay}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setOpenAccordionDay(chartSelectedDay);
    }
  }, [chartSelectedDay]);

  // planId for lesson API — use user's active plan id (from dashboard) or template plan id
  const planId = dashboard?.activePlan?.id ?? plan?.id ?? '';

  const fetchLessonsForDay = useCallback(async (dayNum: number) => {
    if (!email || !planId) {
      if (import.meta.env.DEV) console.log('[LearningPlan] fetchLessonsForDay skipped: email=', !!email, 'planId=', planId);
      return;
    }
    setLessonsLoadingByDay((prev) => ({ ...prev, [dayNum]: true }));
    try {
      const list = await userLessonApi.getByPlanAndDay(email, planId, dayNum);
      if (import.meta.env.DEV) {
        console.log('[LearningPlan] getByPlanAndDay', { dayNum, planId, count: list?.length ?? 0, list });
      }
      setLessonsByDay((prev) => ({ ...prev, [dayNum]: list }));
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[LearningPlan] getByPlanAndDay failed', { dayNum, planId, err });
      setLessonsByDay((prev) => ({ ...prev, [dayNum]: [] }));
    } finally {
      setLessonsLoadingByDay((prev) => ({ ...prev, [dayNum]: false }));
    }
  }, [email, planId]);

  // Seed today's lessons from dashboard so day 1 card shows lesson title immediately
  useEffect(() => {
    if (!dashboard?.todaysLessons?.length || !currentDay) return;
    setLessonsByDay((prev) => ({ ...prev, [currentDay]: dashboard.todaysLessons ?? [] }));
  }, [dashboard?.todaysLessons, currentDay]);

  // Prefetch lessons for all days so every day card shows the lesson title from API
  useEffect(() => {
    if (!email || !planId || displayTotalDays < 1) return;
    for (let d = 1; d <= displayTotalDays; d++) {
      fetchLessonsForDay(d);
    }
  }, [email, planId, displayTotalDays, fetchLessonsForDay]);

  // When day accordion opens, fetch lessons for that day (today uses todaysLessons from dashboard)
  useEffect(() => {
    if (!openAccordionDay || openAccordionDay === currentDay) return;
    if (email && planId) {
      fetchLessonsForDay(openAccordionDay);
    }
  }, [openAccordionDay, email, planId, currentDay, fetchLessonsForDay]);

  // Fetch tips, nudges, tidbits, hacks, nanos for a day when its accordion opens
  const fetchDayExtraContent = useCallback(async (dayNum: number, dayRaw: DayContent) => {
    const tipIds = (dayRaw.tipIds ?? dayRaw.tip_ids ?? []) as string[];
    const nudgeIds = (dayRaw.nudgeIds ?? dayRaw.nudge_ids ?? []) as string[];
    const tidbitIds = (dayRaw.tidbitIds ?? dayRaw.tidbit_ids ?? []) as string[];
    const hackIds = (dayRaw.hackIds ?? dayRaw.hack_ids ?? []) as string[];
    const nanoIds = (dayRaw.nanoIds ?? dayRaw.nano_ids ?? []) as string[];
    if (
      tipIds.length === 0 &&
      nudgeIds.length === 0 &&
      tidbitIds.length === 0 &&
      hackIds.length === 0 &&
      nanoIds.length === 0
    ) {
      setDayExtraContent((prev) => ({
        ...prev,
        [dayNum]: { tips: [], nudges: [], tidbits: [], hacks: [], nanos: [] },
      }));
      return;
    }
    setDayExtraContentLoading((prev) => ({ ...prev, [dayNum]: true }));
    try {
      const [tipsRes, nudgesRes, tidbitsRes, hacksRes, nanosRes] = await Promise.all([
        Promise.allSettled(tipIds.map((id) => userTipApi.getById(id))),
        Promise.allSettled(nudgeIds.map((id) => userNudgeApi.getById(id))),
        Promise.allSettled(tidbitIds.map((id) => userTidbitApi.getById(id))),
        Promise.allSettled(hackIds.map((id) => userHackApi.getById(id))),
        Promise.allSettled(nanoIds.map((id) => userNanoApi.getById(id))),
      ]);
      const tips = tipsRes.filter((r) => r.status === 'fulfilled').map((r) => (r as PromiseFulfilledResult<Tip>).value);
      const nudges = nudgesRes.filter((r) => r.status === 'fulfilled').map((r) => (r as PromiseFulfilledResult<Nudge>).value);
      const tidbits = tidbitsRes.filter((r) => r.status === 'fulfilled').map((r) => (r as PromiseFulfilledResult<Tidbit>).value);
      const hacks = hacksRes.filter((r) => r.status === 'fulfilled').map((r) => (r as PromiseFulfilledResult<Hack>).value);
      const nanos = nanosRes.filter((r) => r.status === 'fulfilled').map((r) => (r as PromiseFulfilledResult<Nano>).value);
      setDayExtraContent((prev) => ({
        ...prev,
        [dayNum]: { tips, nudges, tidbits, hacks, nanos },
      }));
    } catch {
      setDayExtraContent((prev) => ({
        ...prev,
        [dayNum]: { tips: [], nudges: [], tidbits: [], hacks: [], nanos: [] },
      }));
    } finally {
      setDayExtraContentLoading((prev) => ({ ...prev, [dayNum]: false }));
    }
  }, []);

  useEffect(() => {
    if (!openAccordionDay) return;
    const dayRaw = allDays.find((d) => d.dayNumber === openAccordionDay);
    if (!dayRaw || dayExtraContent[openAccordionDay] !== undefined) return;
    fetchDayExtraContent(openAccordionDay, dayRaw);
  }, [openAccordionDay, allDays, dayExtraContent, fetchDayExtraContent]);

  const planForJourney = dashboard?.activePlan ?? learningPlanFromApi ?? null;
  useEffect(() => {
    const looksLikeId = (s: string): boolean =>
      !s || s.length < 10 ? false : /^[0-9a-f-]{20,}$/i.test(s) || /^[0-9a-f]{24}$/i.test(s);
    const journeyPlan = (learningPlanFromApi ?? planForJourney) as Record<string, unknown> | null;
    const dashboardRaw = dashboard as Record<string, unknown> | null | undefined;
    if (!journeyPlan && !profile?.bio) {
      setJourneyContent({ wins: [], painPoints: [], skills: [] });
      setPrimaryWinDetail(null);
      return;
    }
    setPrimaryWinDetail(null);
    let cancelled = false;
    (async () => {
      let winFromDedicatedApi: { name: string; description?: string } | null = null;
      let winsFromApi: { name: string; description?: string }[] = [];
      if (email) {
        try {
          const wins = await userLearningPlanApi.getWinsForUser(email);
          if (wins.length > 0) {
            winFromDedicatedApi = { name: wins[0].name, description: wins[0].description };
            winsFromApi = wins.map((w) => ({ name: w.name, description: w.description }));
          }
        } catch {
          winFromDedicatedApi = null;
          winsFromApi = [];
        }
      }
      if (cancelled) return;
      const plan = journeyPlan ?? {};
      const activePlanWin = dashboardRaw?.activePlan && typeof dashboardRaw.activePlan === 'object' ? (dashboardRaw.activePlan as Record<string, unknown>).win : undefined;
      let planWinRaw: unknown = plan.win ?? (plan as { wins?: unknown }).wins ?? activePlanWin ?? dashboardRaw?.win ?? dashboardRaw?.winId;
      if (Array.isArray(planWinRaw) && planWinRaw.length > 0) planWinRaw = planWinRaw[0];
      const planWinObj = typeof planWinRaw === 'object' && planWinRaw !== null
        ? (planWinRaw as { winDescription?: string; win_description?: string; winId?: string; win_id?: string })
        : null;
      const planWinDesc =
        (planWinObj?.winDescription ?? planWinObj?.win_description ?? (typeof planWinRaw === 'string' ? planWinRaw : '') ?? (plan as { winDescription?: string; win_description?: string }).winDescription ?? (plan as { winDescription?: string; win_description?: string }).win_description ?? '')?.trim() || '';
      const planWinId = planWinObj?.winId ?? planWinObj?.win_id ?? (typeof planWinRaw === 'string' && looksLikeId(planWinRaw) ? planWinRaw : null) ?? (typeof dashboardRaw?.winId === 'string' && looksLikeId(dashboardRaw.winId as string) ? (dashboardRaw.winId as string) : null);
      const bioWins = (profile?.bio as { winsLookingFor?: string[]; winsLookingForIds?: string[] } | undefined);
      const hasPlanWinId = !!(planWinId && looksLikeId(planWinId));
      const initialWins = [
        ...(planWinDesc && !looksLikeId(planWinDesc) && !hasPlanWinId ? [planWinDesc] : []),
        ...(Array.isArray(bioWins?.winsLookingFor)
          ? bioWins.winsLookingFor.filter((w): w is string => typeof w === 'string' && w.trim().length > 0 && !looksLikeId(w))
          : []),
      ];
      const winIdsToResolve = [...new Set([
        ...(hasPlanWinId ? [planWinId!] : []),
        ...(Array.isArray(bioWins?.winsLookingForIds) ? bioWins.winsLookingForIds.filter((id): id is string => typeof id === 'string' && looksLikeId(id)) : []),
      ])];
      const planPains = (plan.painPoints ?? plan.pain_points ?? []) as Array<{ painPointDescription?: string; pain_point_description?: string; painPointId?: string }>;
      const bioPains = (profile?.bio as { painPoints?: { painPointDescription?: string; pain_point_description?: string; painPointId?: string }[] } | undefined)?.painPoints ?? [];
      const initialPainPoints = [
        ...planPains
          .map((p) => p.painPointDescription ?? p.pain_point_description ?? '')
          .filter((p): p is string => typeof p === 'string' && p.trim().length > 0 && !looksLikeId(p)),
        ...bioPains
          .map((p) => p.painPointDescription ?? (p as { pain_point_description?: string }).pain_point_description ?? '')
          .filter((p): p is string => typeof p === 'string' && p.trim().length > 0 && !looksLikeId(p)),
      ];
      const painIdsToResolve = [...new Set([
        ...planPains.filter((p) => p.painPointId && looksLikeId(p.painPointId) && !(p.painPointDescription ?? p.pain_point_description)?.trim()).map((p) => p.painPointId!),
        ...bioPains.filter((p) => p.painPointId && looksLikeId(p.painPointId) && !(p.painPointDescription ?? (p as { pain_point_description?: string }).pain_point_description)?.trim()).map((p) => p.painPointId!),
      ])];
      const skillSets = (plan.addressingSkillSets ?? plan.addressing_skill_sets ?? []) as Array<{ skillName?: string; skill_name?: string; skillId?: string }>;
      const bioSkills = (profile?.bio as { skills?: { name?: string; skillId?: string }[] } | undefined)?.skills ?? [];
      const initialSkills = [
        ...skillSets
          .map((s) => s.skillName ?? s.skill_name ?? '')
          .filter((name: string) => name.length > 0 && !looksLikeId(name)),
        ...bioSkills
          .map((s) => s.name ?? '')
          .filter((name: string) => typeof name === 'string' && name.trim().length > 0 && !looksLikeId(name)),
      ];
      const skillIdsToResolve = [...new Set([
        ...skillSets
          .filter((s) => (s.skillId && looksLikeId(s.skillId)) && !(s.skillName ?? s.skill_name)?.trim())
          .map((s) => s.skillId!),
        ...bioSkills
          .filter((s) => s.skillId && looksLikeId(s.skillId) && !(s.name ?? '')?.trim())
          .map((s) => s.skillId!),
      ])];

      const winsList =
        winsFromApi.length > 0
          ? [...winsFromApi.map((w) => w.name), ...initialWins].filter((w, i, a) => a.indexOf(w) === i)
          : winFromDedicatedApi
            ? [winFromDedicatedApi.name, ...initialWins].filter((w, i, a) => a.indexOf(w) === i)
            : initialWins;

      if (winIdsToResolve.length === 0 && painIdsToResolve.length === 0 && skillIdsToResolve.length === 0) {
        if (!cancelled) {
          setJourneyContent({ wins: winsList, painPoints: initialPainPoints, skills: initialSkills });
          setPrimaryWinDetail(winFromDedicatedApi ?? (planWinDesc && !hasPlanWinId ? { name: planWinDesc } : null));
        }
        return;
      }
      const winPromises = winIdsToResolve.map((id, i) =>
        winApi.getById(id).then((w) => ({
          name: w.name || w.description || '',
          description: w.description || w.summary || undefined,
          isFirst: i === 0,
          requiredSkills: w.requiredSkills ?? (w as { required_skills?: { skillId?: string; skill_id?: string }[] }).required_skills ?? [],
        }))
      );
      Promise.allSettled(winPromises).then((winResults) => {
        if (cancelled) return;
        let primaryDetail: { name: string; description?: string } | null = null;
        const resolvedWinStrings: string[] = [];
        const skillIdsFromWins: string[] = [];
        winResults.forEach((r) => {
          if (r?.status === 'fulfilled' && r.value && typeof r.value === 'object' && 'name' in r.value) {
            const v = r.value as { name: string; description?: string; isFirst?: boolean; requiredSkills?: { skillId?: string; skill_id?: string }[] };
            if (v.name && !looksLikeId(v.name)) resolvedWinStrings.push(v.name);
            if (v.isFirst && v.name) primaryDetail = { name: v.name, description: v.description };
            const skills = v.requiredSkills ?? [];
            skills.forEach((rs: { skillId?: string; skill_id?: string }) => {
              const sid = rs.skillId ?? rs.skill_id;
              if (sid && looksLikeId(sid)) skillIdsFromWins.push(sid);
            });
          }
        });
        const skillIdsToResolveFinal = [...new Set([...skillIdsToResolve, ...skillIdsFromWins])];
        const painPromises = painIdsToResolve.map((id) => painPointApi.getById(id).then((p) => p.name || p.description || ''));
        const skillPromises = skillIdsToResolveFinal.map((id) => skillApi.getById(id).then((s) => s.name || ''));
        Promise.allSettled([...painPromises, ...skillPromises]).then((results) => {
          if (cancelled) return;
          const painCount = painIdsToResolve.length;
          const skillCount = skillIdsToResolveFinal.length;
          const resolvedPains = results.slice(0, painCount).map((r) => (r.status === 'fulfilled' ? r.value : '')).filter((s): s is string => typeof s === 'string' && s.trim().length > 0 && !looksLikeId(s));
          const resolvedSkills = results.slice(painCount, painCount + skillCount).map((r) => (r.status === 'fulfilled' ? r.value : '')).filter((s): s is string => typeof s === 'string' && s.trim().length > 0 && !looksLikeId(s));
          if (!cancelled) {
            setPrimaryWinDetail(winFromDedicatedApi ?? primaryDetail);
            const mergedWins =
              winsFromApi.length > 0
                ? [...winsFromApi.map((w) => w.name), ...initialWins, ...resolvedWinStrings].filter((w, i, a) => a.indexOf(w) === i)
                : winFromDedicatedApi
                  ? [winFromDedicatedApi.name, ...initialWins, ...resolvedWinStrings].filter((w, i, a) => a.indexOf(w) === i)
                  : [...initialWins, ...resolvedWinStrings];
            setJourneyContent({
              wins: mergedWins,
              painPoints: [...initialPainPoints, ...resolvedPains],
              skills: [...initialSkills, ...resolvedSkills].filter((s, i, a) => a.indexOf(s) === i),
            });
          }
        }).catch(() => {
          if (!cancelled) {
            setPrimaryWinDetail(winFromDedicatedApi ?? primaryDetail);
            const mergedWins =
              winsFromApi.length > 0
                ? [...winsFromApi.map((w) => w.name), ...initialWins, ...resolvedWinStrings].filter((w, i, a) => a.indexOf(w) === i)
                : winFromDedicatedApi
                  ? [winFromDedicatedApi.name, ...initialWins, ...resolvedWinStrings].filter((w, i, a) => a.indexOf(w) === i)
                  : [...initialWins, ...resolvedWinStrings];
            setJourneyContent({
              wins: mergedWins,
              painPoints: initialPainPoints,
              skills: initialSkills,
            });
          }
        });
      }).catch(() => {
        if (!cancelled) {
          setPrimaryWinDetail(winFromDedicatedApi ?? (planWinDesc && !hasPlanWinId ? { name: planWinDesc } : null));
          setJourneyContent({ wins: winsList, painPoints: initialPainPoints, skills: initialSkills });
        }
      });
    })();
    return () => { cancelled = true; };
  }, [learningPlanFromApi, planForJourney, profile, dashboard, email]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center pt-24 px-4">
          <BrandedLoading message="Completing authentication…" iconSize="md" />
        </main>
      </div>
    );
  }
  if (!isAuthenticated) {
    return null;
  }

  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-violet-500/[0.04]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.1),transparent_50%)] pointer-events-none" aria-hidden />
        <Header />
        <main className="relative flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] pt-24 px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full text-center space-y-8"
          >
            <div className="rounded-3xl bg-gradient-to-br from-primary/15 to-violet-500/15 p-10 w-fit mx-auto border border-primary/10 shadow-lg">
              <BookOpen className="w-20 h-20 text-primary mx-auto" />
            </div>
            <div className="space-y-3">
              <h1 className="text-4xl font-bold tracking-tight text-foreground">Your Learning Plan</h1>
              <p className="text-lg text-muted-foreground">
                Sign in or complete onboarding to get a personalized learning path and daily actions.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button size="lg" onClick={() => navigate('/onboarding')} className="rounded-xl shadow-md">
                Get started
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/coach')} className="rounded-xl">
                Go to Coach
              </Button>
            </div>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.12),transparent)]">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] pt-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <BrandedLoading message="Loading your learning plan…" iconSize="lg" />
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] pt-24 px-4">
          <Card className="max-w-md w-full rounded-2xl border-2">
            <CardContent className="pt-8 pb-8 text-center">
              <p className="text-destructive font-medium">{error.message}</p>
              <Button className="mt-6 rounded-xl" onClick={fetchData}>
                Try again
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (!plan) {
    // Determine if user is already onboarded (recovery scenario) vs first-time
    const isOnboarded = profile?.isOnboardingCompleted === true;

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-violet-500/[0.04]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.1),transparent_50%)] pointer-events-none" aria-hidden />
        <Header />
        <main className="relative flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] pt-24 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg w-full rounded-3xl border-2 border-border bg-card/95 backdrop-blur-xl p-12 text-center shadow-xl shadow-primary/5"
          >
            <div className="rounded-3xl bg-gradient-to-br from-primary/15 to-violet-500/15 p-8 w-fit mx-auto mb-6 border border-primary/10">
              <Sparkles className="w-20 h-20 text-primary" />
            </div>
            {isOnboarded ? (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Your learning plan needs setup</h1>
                <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                  Your personalized learning plan wasn't generated during onboarding. Let's create it now — it only takes a minute.
                </p>
                <Button size="lg" className="rounded-xl shadow-md" onClick={() => setLpRecoveryModalOpen(true)}>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Generate My Plan
                </Button>
                <RecoverySurveyModal
                  open={lpRecoveryModalOpen}
                  onOpenChange={setLpRecoveryModalOpen}
                  userId={email}
                  existingSessionId={null}
                  source="LEARNING_PLAN_PAGE"
                />
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">No learning plan yet</h1>
                <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                  Complete onboarding to receive a personalized plan with daily lessons and actions tailored to your goals.
                </p>
                <Button size="lg" className="rounded-xl shadow-md" onClick={() => navigate('/onboarding')}>
                  Create my plan
                </Button>
              </>
            )}
            <p className="text-sm text-muted-foreground mt-6">
              Already have an account?{' '}
              <button type="button" className="text-primary underline font-medium" onClick={() => navigate('/coach')}>
                Open Coach
              </button>
            </p>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  const displayName = profile
    ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.userName || profile.email
    : email?.split('@')[0] ?? 'You';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-violet-500/[0.03] dark:to-violet-500/[0.06]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.08),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.12),transparent_50%)] pointer-events-none" aria-hidden />
      <Header />
      <main className="relative pt-20 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <EmailVerificationBanner email={email ?? profile?.email} emailVerified={profile?.emailVerified} className="mb-4" />
          <SubscriptionGuard className="mb-4" />
          {/* Hero header */}
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="rounded-3xl border border-border/60 bg-gradient-to-br from-card/95 via-card to-violet-500/5 dark:from-card dark:via-card dark:to-violet-500/10 p-6 sm:p-8 shadow-lg shadow-primary/5">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary via-violet-600 to-fuchsia-600 flex items-center justify-center shadow-xl shadow-primary/25 shrink-0 ring-4 ring-primary/10">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                      {plan.title}
                    </h1>
                    <p className="text-muted-foreground mt-1.5 text-base">
                      Your personalized learning journey
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-5">
                      <Badge variant="secondary" className="gap-1.5 rounded-lg px-3 py-1 font-medium">
                        <Calendar className="w-3.5 h-3.5" />
                        Day {currentDay} of {displayTotalDays}
                      </Badge>
                      <Badge variant="outline" className="rounded-lg px-3 py-1">
                        {daysCompleted} done · {daysRemaining} to go
                        {overdueDayNumbers.length > 0 && (
                          <span className="ml-1 text-amber-600 dark:text-amber-400"> · {overdueDayNumbers.length} overdue</span>
                        )}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0 rounded-2xl bg-muted/40 dark:bg-muted/20 px-4 py-3 border border-border/50">
                  <ProfileAvatar src={profilePictureUrl} displayName={displayName} size="lg" className="ring-2 ring-background shadow-md" />
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[180px]">{email}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.header>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main: Path timeline + today */}
            <div className="lg:col-span-2 space-y-8">

              {/* ============================================================ */}
              {/* PLAN COMPLETION BANNER + REFLECTION + ASSESSMENT */}
              {/* Shows when plan status is COMPLETED */}
              {/* ============================================================ */}
              {plan?.status === 'COMPLETED' && (
                <div className="space-y-4">
                  {/* Celebration banner */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Card className="overflow-hidden border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-primary/5">
                      <CardContent className="py-8 text-center space-y-4">
                        <div className="flex justify-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 flex items-center justify-center shadow-lg">
                            <Trophy className="w-8 h-8 text-amber-900" />
                          </div>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground">Plan Completed!</h2>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto">
                          You completed all {totalDays} days of <span className="font-medium text-foreground">{plan.title}</span>. Take a moment to reflect before starting your next plan.
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Reflection form */}
                  {!reflectionDone && (
                    <ReflectionSection
                      userId={email ?? ''}
                      learningPathId={dashboard?.learningPath?.id ?? ''}
                      planId={plan.id ?? ''}
                      planTitle={plan.title}
                      onComplete={() => setReflectionDone(true)}
                      onSkip={() => setReflectionDone(true)}
                    />
                  )}

                  {/* Assessment quiz (after reflection) */}
                  {reflectionDone && !assessmentDone && (() => {
                    // Find assessment from plan's final_assessment_id or last day's assessment_id
                    let assessId: string | undefined;
                    if (modules.length > 0) {
                      const mod = modules[0] as Record<string, unknown>;
                      assessId = (mod.finalAssessmentId ?? mod.final_assessment_id) as string | undefined;
                      if (!assessId) {
                        const daily = (mod.dailyContents ?? mod.daily_contents) as Array<Record<string, unknown>> | undefined;
                        if (daily && daily.length > 0) {
                          assessId = (daily[daily.length - 1].assessmentId ?? daily[daily.length - 1].assessment_id) as string | undefined;
                        }
                      }
                    }
                    if (!assessId) {
                      // No assessment — auto-skip
                      setTimeout(() => setAssessmentDone(true), 0);
                      return null;
                    }
                    return (
                      <AssessmentSection
                        userId={email ?? ''}
                        assessmentId={assessId}
                        onComplete={() => setAssessmentDone(true)}
                        onSkip={() => setAssessmentDone(true)}
                      />
                    );
                  })()}

                  {/* Start next plan button (after reflection + assessment) */}
                  {reflectionDone && assessmentDone && dashboard?.followUpPlanStatus === 'READY' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Card className="border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-violet-500/5">
                        <CardContent className="py-6 text-center space-y-4">
                          <div className="flex justify-center">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                              <Sparkles className="w-7 h-7 text-white" />
                            </div>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-foreground">Your next learning plan is ready!</h3>
                            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                              Based on your progress, we&apos;ve prepared a personalized follow-up plan to continue your growth.
                            </p>
                          </div>
                          <Button
                            size="lg"
                            disabled={startingNextPlan}
                            onClick={async () => {
                              setStartingNextPlan(true);
                              try {
                                // Find the next PLANNED plan from journey data or learning path details
                                const journeyPlans = dashboard?.journeyPlans ?? [];
                                let nextPlanId: string | undefined = journeyPlans.find((p: { status: string }) => p.status === 'PLANNED')?.planId;

                                // Fallback: find from learning path details
                                if (!nextPlanId) {
                                  const pathDetails = dashboard?.learningPath?.learningPathDetails ?? [];
                                  const currentPlanId = plan?.id;
                                  let foundCurrent = false;
                                  for (const d of pathDetails) {
                                    if (foundCurrent) {
                                      nextPlanId = d.userLearningPlanId;
                                      break;
                                    }
                                    if (d.userLearningPlanId === currentPlanId) foundCurrent = true;
                                  }
                                }

                                if (nextPlanId) {
                                  await userLearningPlanApi.start(nextPlanId);
                                }
                                window.location.reload();
                              } catch (e) {
                                console.error('Failed to start next plan:', e);
                                setStartingNextPlan(false);
                              }
                            }}
                            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
                          >
                            {startingNextPlan ? 'Starting...' : 'Start Next Plan'}
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}

                  {/* No follow-up available — go to onboarding */}
                  {reflectionDone && assessmentDone && (!dashboard?.followUpPlanStatus || dashboard.followUpPlanStatus === 'NONE') && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Card className="border-2 border-primary/20">
                        <CardContent className="py-6 text-center space-y-3">
                          <p className="text-sm text-muted-foreground">Ready for your next learning journey?</p>
                          <Button onClick={() => navigate('/onboarding')} className="gap-2">
                            <Sparkles className="w-4 h-4" /> Create New Plan
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Progress chart — drill-down */}
              <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <Card className="rounded-2xl border-2 overflow-hidden shadow-md shadow-primary/5 bg-card/98">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        Progress over time
                      </CardTitle>
                      <span className="text-2xl font-bold text-primary tabular-nums">{Math.round(progress)}%</span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2 pb-6">
                    <ProgressLineChart
                      totalDays={displayTotalDays}
                      daysCompleted={daysCompleted}
                      completedDayNumbers={completedDayNumbers.length > 0 ? completedDayNumbers : undefined}
                      currentDay={currentDay}
                      selectedDay={chartSelectedDay}
                      onDayClick={(d) => setChartSelectedDay(d)}
                    />
                  </CardContent>
                </Card>
              </motion.section>

              {/* Today's focus */}
              <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="rounded-2xl border-2 overflow-hidden bg-gradient-to-br from-primary/8 via-card to-violet-500/8 dark:from-primary/12 dark:to-violet-500/12 shadow-md">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15">
                        <Zap className="w-5 h-5 text-primary" />
                      </div>
                      <CardTitle className="text-xl">Today&apos;s focus</CardTitle>
                      {todaysContent?.status && (
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs',
                            todaysContent.status === 'COMPLETED' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
                            todaysContent.status === 'IN_PROGRESS' && 'bg-primary/15 text-primary border-primary/20',
                            todaysContent.status === 'PENDING' && 'bg-muted text-muted-foreground'
                          )}
                        >
                          {todaysContent.status === 'COMPLETED' ? 'Completed' : todaysContent.status === 'IN_PROGRESS' ? 'In progress' : todaysContent.status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground">
                      {todaysContent?.title ?? plan.title}
                    </p>
                    {todaysContent?.description && (
                      <p className="text-sm text-muted-foreground mt-1">{todaysContent.description}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {todaysContent?.learningObjectives && todaysContent.learningObjectives.length > 0 && (
                      <ul className="space-y-2">
                        {todaysContent.learningObjectives.map((obj, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <Circle className="w-1.5 h-1.5 fill-primary text-primary shrink-0" />
                            {obj}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="rounded-xl bg-amber-500/10 dark:bg-amber-500/15 border border-amber-500/20 p-4">
                      <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        Nudges & tips
                      </h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Check the Coach for your daily nudge. Complete today&apos;s lesson to unlock more.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate('/coach')} className="rounded-lg">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Open Coach
                        </Button>
                        <Button
                          size="sm"
                          className="rounded-lg gap-1.5"
                          onClick={() => {
                            const label = todaysContent?.title ?? plan?.title ?? `Day ${currentDay}`;
                            const description = (todaysContent?.description ?? '') as string;
                            const objectivesRaw =
                              ((todaysContent as unknown as { learningObjectives?: unknown } | null)?.learningObjectives ?? []) as unknown;
                            const objectives = Array.isArray(objectivesRaw)
                              ? objectivesRaw.filter((o) => typeof o === 'string' && o.trim()).map((o) => o.trim())
                              : [];
                            const promptParts: string[] = [`Let's start Day ${currentDay}: ${label}`];
                            if (description.trim()) {
                              promptParts.push('');
                              promptParts.push('Day lesson context:');
                              promptParts.push(description.trim());
                            }
                            if (objectives.length > 0) {
                              promptParts.push('');
                              promptParts.push('Learning objectives:');
                              promptParts.push(objectives.map((o) => `- ${o}`).join('\n'));
                            }
                            promptParts.push('');
                            promptParts.push('Start by summarizing the lesson in 3-5 bullets, then ask me one question to begin.');

                            sessionStorage.setItem(
                              COACH_START_LESSON_KEY,
                              JSON.stringify({ day: currentDay, label, prompt: promptParts.join('\n') })
                            );
                            navigate('/coach');
                          }}
                        >
                          <Play className="w-4 h-4" />
                          Start in Coach
                        </Button>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold mb-3">Actions for today</h4>
                      {todaysLessons.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">
                          No lessons scheduled. Check back tomorrow or explore the Coach.
                        </p>
                      ) : (
                        <ul className="space-y-3">
                          {todaysLessons.map((lesson) => (
                            <LessonCard
                              key={lesson.id}
                              lesson={lesson}
                              actionLoading={actionLoading}
                              onView={handleLessonView}
                              onStart={handleLessonStart}
                              onComplete={handleLessonComplete}
                              onStartInCoach={() => {
                                const dayLabel = todaysContent?.title ?? plan?.title ?? `Day ${currentDay}`;
                                const selectedLessonTitle =
                                  ((lesson as unknown as { title?: string; lessonTitle?: string; name?: string })?.title ??
                                    (lesson as unknown as { title?: string; lessonTitle?: string; name?: string })?.lessonTitle ??
                                    (lesson as unknown as { title?: string; lessonTitle?: string; name?: string })?.name ??
                                    '') as string;
                                const description = (todaysContent?.description ?? '') as string;
                                const objectivesRaw =
                                  ((todaysContent as unknown as { learningObjectives?: unknown } | null)?.learningObjectives ?? []) as unknown;
                                const objectives = Array.isArray(objectivesRaw)
                                  ? objectivesRaw.filter((o) => typeof o === 'string' && o.trim()).map((o) => o.trim())
                                  : [];
                                const promptParts: string[] = [`Let's start Day ${currentDay}: ${dayLabel}`];
                                if (description.trim()) {
                                  promptParts.push('');
                                  promptParts.push('Day lesson context:');
                                  promptParts.push(description.trim());
                                }
                                if (objectives.length > 0) {
                                  promptParts.push('');
                                  promptParts.push('Learning objectives:');
                                  promptParts.push(objectives.map((o) => `- ${o}`).join('\n'));
                                }
                                if (selectedLessonTitle.trim()) {
                                  promptParts.push('');
                                  promptParts.push(`Selected lesson: ${selectedLessonTitle.trim()}`);
                                }
                                promptParts.push('');
                                promptParts.push('Start by summarizing the lesson in 3-5 bullets, then ask me one question to begin.');

                                sessionStorage.setItem(
                                  COACH_START_LESSON_KEY,
                                  JSON.stringify({ day: currentDay, label: dayLabel, prompt: promptParts.join('\n') })
                                );
                                navigate('/coach');
                              }}
                              coachLabel="Coach"
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.section>

              {/* Overdue — when backend reports overdue days */}
              {overdueDayNumbers.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                >
                  <Card className="rounded-2xl border-2 border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent overflow-hidden">
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15">
                          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <CardTitle className="text-lg">Overdue</CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Complete these days to stay on track.
                      </p>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="flex flex-wrap gap-2">
                        {overdueDayNumbers.map((d) => {
                          const dayLabel = lessonsByDay[d]?.[0]?.title ?? `Day ${d}`;
                          return (
                            <li key={d}>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenAccordionDay(d);
                                  document.getElementById(`day-${d}`)?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="inline-flex items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors"
                              >
                                Day {d}: {typeof dayLabel === 'string' && dayLabel.length > 40 ? `${dayLabel.slice(0, 37)}…` : dayLabel}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.section>
              )}

              {/* Compact stats bar */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="flex flex-wrap items-center gap-2"
              >
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  <Calendar className="w-3 h-3" /> Day {currentDay} of {totalDays}
                </span>
                {daysCompleted > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="w-3 h-3" /> {daysCompleted} completed
                  </span>
                )}
                {todayTipCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs text-amber-700 dark:text-amber-400">
                    <Lightbulb className="w-3 h-3" /> {todayTipCount} tips
                  </span>
                )}
                {todayNudgeCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-1 text-xs text-blue-700 dark:text-blue-400">
                    <MessageCircle className="w-3 h-3" /> {todayNudgeCount} nudges
                  </span>
                )}
                {todayTidbitCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2.5 py-1 text-xs text-violet-700 dark:text-violet-400">
                    <Sparkles className="w-3 h-3" /> {todayTidbitCount} tidbits
                  </span>
                )}
                {todayHackCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-700 dark:text-emerald-400">
                    <Zap className="w-3 h-3" /> {todayHackCount} hacks
                  </span>
                )}
                {todayNanoCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2.5 py-1 text-xs text-sky-700 dark:text-sky-400">
                    <Target className="w-3 h-3" /> {todayNanoCount} nanos
                  </span>
                )}
              </motion.div>

              {/* Full path timeline — all days */}
              <motion.section
                ref={pathTimelineRef}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  Full learning path
                </h2>
                <div className="space-y-2">
                  {allDays.map((dayRaw) => {
                    const dayNum = dayRaw.dayNumber;
                    // Use only lesson title from API so we never show wrong/cached/goal-based labels (e.g. from shortTermGoals)
                    const lessonTitle = lessonsByDay[dayNum]?.[0]?.title;
                    const label = lessonTitle ?? `Day ${dayNum}`;
                    const isToday = dayNum === currentDay;
                    const isOverdue = overdueDayNumbers.includes(dayNum);
                    const isCompleted = completedDayNumbers.includes(dayNum);
                    const isUpcoming = dayNum > currentDay;
                    const isOpen = openAccordionDay === dayNum;
                    return (
                      <Card key={dayNum} id={`day-${dayNum}`} className="overflow-hidden rounded-xl border-2">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-muted/30 transition-colors"
                          onClick={() => setOpenAccordionDay(isOpen ? null : dayNum)}
                          aria-expanded={isOpen}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={`
                                w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm
                                ${isOverdue ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400' : ''}
                                ${isCompleted ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' : ''}
                                ${isToday ? 'bg-primary text-primary-foreground' : ''}
                                ${!isOverdue && !isCompleted && !isToday ? 'bg-muted text-muted-foreground' : ''}
                              `}
                            >
                              {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : dayNum}
                            </div>
                            <div className="min-w-0">
                              <span className="font-semibold text-foreground block truncate">
                                Day {dayNum}: {label}
                              </span>
                              {isToday && (
                                <Badge variant="secondary" className="mt-1 text-xs">Today</Badge>
                              )}
                              {isOverdue && (
                                <Badge variant="outline" className="mt-1 text-xs border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10">Catch up</Badge>
                              )}
                              {isUpcoming && (
                                <span className="mt-1 text-xs text-muted-foreground">Upcoming</span>
                              )}
                            </div>
                          </div>
                          {isOpen ? <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />}
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <CardContent className="pt-0 pb-4 border-t border-border">
                                {dayRaw.description && (
                                  <p className="text-sm text-muted-foreground mt-3">{dayRaw.description}</p>
                                )}
                                {dayRaw.learningObjectives && dayRaw.learningObjectives.length > 0 && (
                                  <ul className="mt-2 space-y-1.5 mb-4">
                                    {dayRaw.learningObjectives.map((obj, i) => (
                                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Circle className="w-1.5 h-1.5 fill-primary text-primary shrink-0" />
                                        {obj}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                <h4 className="text-sm font-semibold mb-3 mt-4">Lesson</h4>
                                {(() => {
                                  const dayLessons = dayNum === currentDay && todaysLessons.length > 0
                                    ? todaysLessons
                                    : (lessonsByDay[dayNum] ?? []);
                                  const isLoading = dayNum !== currentDay && lessonsLoadingByDay[dayNum];
                                  if (isLoading) {
                                    return (
                                      <div className="py-6">
                                        <BrandedLoading
                                          message="Loading lessons…"
                                          iconSize="sm"
                                          layout="inline"
                                        />
                                      </div>
                                    );
                                  }
                                  if (dayLessons.length === 0) {
                                    return (
                                      <div className="rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center">
                                        <p className="text-sm text-muted-foreground mb-3">No lessons found for this day.</p>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="rounded-lg gap-1.5"
                                          onClick={() => {
                                            const description = (dayRaw.description ?? '') as string;
                                            const objectivesRaw =
                                              (dayRaw.learningObjectives ?? dayRaw.learning_objectives ?? []) as unknown;
                                            const objectives = Array.isArray(objectivesRaw)
                                              ? objectivesRaw.filter((o) => typeof o === 'string' && o.trim()).map((o) => o.trim())
                                              : [];
                                            const promptParts: string[] = [`Let's start Day ${dayNum}: ${label}`];
                                            if (description.trim()) {
                                              promptParts.push('');
                                              promptParts.push('Day lesson context:');
                                              promptParts.push(description.trim());
                                            }
                                            if (objectives.length > 0) {
                                              promptParts.push('');
                                              promptParts.push('Learning objectives:');
                                              promptParts.push(objectives.map((o) => `- ${o}`).join('\n'));
                                            }
                                            promptParts.push('');
                                            promptParts.push('Start by summarizing the lesson in 3-5 bullets, then ask me one question to begin.');

                                            sessionStorage.setItem(
                                              COACH_START_LESSON_KEY,
                                              JSON.stringify({ day: dayNum, label, prompt: promptParts.join('\n') })
                                            );
                                            navigate('/coach');
                                          }}
                                        >
                                          <MessageCircle className="w-4 h-4" />
                                          Chat in Coach for more
                                        </Button>
                                      </div>
                                    );
                                  }
                                  return (
                                    <ul className="space-y-3">
                                      {dayLessons.map((lesson) => (
                                        <LessonCard
                                        key={lesson.id}
                                        lesson={lesson}
                                        actionLoading={actionLoading}
                                        onView={handleLessonView}
                                        onStart={handleLessonStart}
                                        onComplete={handleLessonComplete}
                                        onStartInCoach={() => {
                                          const selectedLessonTitle =
                                            ((lesson as unknown as { title?: string; lessonTitle?: string; name?: string })
                                              ?.title ??
                                              (lesson as unknown as { title?: string; lessonTitle?: string; name?: string })
                                                ?.lessonTitle ??
                                              (lesson as unknown as { title?: string; lessonTitle?: string; name?: string })
                                                ?.name ??
                                              '') as string;
                                          const description = (dayRaw.description ?? '') as string;
                                          const objectivesRaw =
                                            (dayRaw.learningObjectives ?? dayRaw.learning_objectives ?? []) as unknown;
                                          const objectives = Array.isArray(objectivesRaw)
                                            ? objectivesRaw
                                                .filter((o) => typeof o === 'string' && o.trim())
                                                .map((o) => o.trim())
                                            : [];
                                          const promptParts: string[] = [`Let's start Day ${dayNum}: ${label}`];
                                          if (description.trim()) {
                                            promptParts.push('');
                                            promptParts.push('Day lesson context:');
                                            promptParts.push(description.trim());
                                          }
                                          if (objectives.length > 0) {
                                            promptParts.push('');
                                            promptParts.push('Learning objectives:');
                                            promptParts.push(objectives.map((o) => `- ${o}`).join('\n'));
                                          }
                                          if (selectedLessonTitle.trim()) {
                                            promptParts.push('');
                                            promptParts.push(`Selected lesson: ${selectedLessonTitle.trim()}`);
                                          }
                                          promptParts.push('');
                                          promptParts.push('Start by summarizing the lesson in 3-5 bullets, then ask me one question to begin.');

                                          sessionStorage.setItem(
                                            COACH_START_LESSON_KEY,
                                            JSON.stringify({ day: dayNum, label, prompt: promptParts.join('\n') })
                                          );
                                          navigate('/coach');
                                        }}
                                        coachLabel="Chat in Coach"
                                        />
                                      ))}
                                    </ul>
                                  );
                                })()}
                                {(() => {
                                  const extra = dayExtraContent[dayNum];
                                  const extraLoading = dayExtraContentLoading[dayNum];
                                  const reflectionPrompts = (dayRaw.reflectionPrompts ?? dayRaw.reflection_prompts ?? []) as string[];
                                  if (extraLoading) {
                                    return (
                                      <div className="py-4 mt-4 border-t border-border pt-4">
                                        <BrandedLoading
                                          message="Loading tips, nudges & more…"
                                          iconSize="sm"
                                          layout="inline"
                                        />
                                      </div>
                                    );
                                  }
                                  const tipsWithContent = (extra?.tips ?? []).filter(hasMeaningfulContent);
                                  const nudgesWithContent = (extra?.nudges ?? []).filter(hasMeaningfulContent);
                                  const tidbitsWithContent = (extra?.tidbits ?? []).filter(hasMeaningfulContent);
                                  const hacksWithContent = (extra?.hacks ?? []).filter(hasMeaningfulContent);
                                  const nanosWithContent = (extra?.nanos ?? []).filter(hasMeaningfulContent);
                                  const hasExtra =
                                    tipsWithContent.length > 0 ||
                                    nudgesWithContent.length > 0 ||
                                    tidbitsWithContent.length > 0 ||
                                    hacksWithContent.length > 0 ||
                                    nanosWithContent.length > 0 ||
                                    reflectionPrompts.length > 0;
                                  if (!hasExtra) return null;
                                  const openCoachWithPrompt = (prompt?: string) => {
                                    if (prompt) sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt }));
                                    navigate('/coach');
                                  };
                                  return (
                                    <div className="mt-6 pt-4 border-t border-border space-y-6">
                                      {tipsWithContent.length > 0 && (
                                        <div>
                                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-amber-700 dark:text-amber-300">
                                            <Lightbulb className="w-4 h-4" />
                                            Tips
                                          </h4>
                                          <ul className="space-y-3">
                                            {tipsWithContent.map((t) => (
                                              <li key={t.id ?? t.tipId ?? t.title}>
                                                <DayContentCard item={t} variant="tip" onOpenCoach={openCoachWithPrompt} />
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {nudgesWithContent.length > 0 && (
                                        <div>
                                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-primary">
                                            <MessageCircle className="w-4 h-4" />
                                            Nudges
                                          </h4>
                                          <ul className="space-y-3">
                                            {nudgesWithContent.map((n) => (
                                              <li key={n.id ?? n.nudgeId ?? n.title}>
                                                <DayContentCard item={n} variant="nudge" onOpenCoach={openCoachWithPrompt} />
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {tidbitsWithContent.length > 0 && (
                                        <div>
                                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-violet-600 dark:text-violet-400">
                                            <Sparkles className="w-4 h-4" />
                                            Tidbits
                                          </h4>
                                          <ul className="space-y-3">
                                            {tidbitsWithContent.map((tb) => (
                                              <li key={tb.id ?? tb.tidbitId ?? tb.title}>
                                                <DayContentCard item={tb} variant="tidbit" onOpenCoach={openCoachWithPrompt} />
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {hacksWithContent.length > 0 && (
                                        <div>
                                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                                            <Zap className="w-4 h-4" />
                                            Hacks
                                          </h4>
                                          <ul className="space-y-3">
                                            {hacksWithContent.map((h) => (
                                              <li key={h.id ?? h.hackId ?? h.title}>
                                                <DayContentCard item={h} variant="hack" onOpenCoach={openCoachWithPrompt} />
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {nanosWithContent.length > 0 && (
                                        <div>
                                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-sky-700 dark:text-sky-300">
                                            <Target className="w-4 h-4" />
                                            Nanos
                                          </h4>
                                          <ul className="space-y-3">
                                            {nanosWithContent.map((na) => (
                                              <li key={na.id ?? na.title}>
                                                <DayContentCard item={na} variant="nano" onOpenCoach={openCoachWithPrompt} />
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {reflectionPrompts.length > 0 && (
                                        <div className="rounded-xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-4">
                                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-primary">
                                            <Eye className="w-4 h-4" />
                                            Reflection
                                          </h4>
                                          <ul className="space-y-2">
                                            {reflectionPrompts.map((q, i) => (
                                              <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                                                <span className="text-primary font-semibold shrink-0">{i + 1}.</span>
                                                <span>{q}</span>
                                              </li>
                                            ))}
                                          </ul>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="mt-3 rounded-lg gap-1.5"
                                            onClick={() => openCoachWithPrompt('I want to reflect on today’s learning.')}
                                          >
                                            <MessageCircle className="w-3.5 h-3.5" />
                                            Reflect in Coach
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </CardContent>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Card>
                    );
                  })}
                </div>
              </motion.section>
            </div>

            {/* Sidebar: Profile, progress, completed, stats */}
            <aside className="space-y-6">
              {/* User profile card */}
              <motion.div
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
              >
                <Card className="rounded-2xl border-2 overflow-hidden shadow-md bg-gradient-to-br from-card via-card to-violet-500/5 dark:to-violet-500/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <ProfileAvatar
                        displayName={displayName}
                        size="lg"
                        className="h-14 w-14 rounded-xl ring-2 ring-border/80 shadow-md shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-foreground truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{email}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 h-8 px-2 text-xs text-primary hover:text-primary rounded-lg gap-1"
                          onClick={() => navigate('/profile')}
                        >
                          <User className="w-3.5 h-3.5" />
                          My profile
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Progress + Days Completed (merged) */}
              <motion.div
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="rounded-2xl border-2 overflow-hidden shadow-md bg-gradient-to-b from-card to-card/95">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Progress</p>
                        <p className="text-3xl font-bold text-primary tabular-nums mt-0.5">{Math.round(progress)}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{daysCompleted} completed</p>
                        <p className="text-xs text-muted-foreground">{daysRemaining} remaining</p>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="h-full rounded-full bg-gradient-to-r from-primary to-violet-500"
                      />
                    </div>
                    {daysCompleted > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {(completedDayNumbers.length > 0 ? completedDayNumbers : Array.from({ length: daysCompleted }, (_, i) => i + 1))
                          .slice(0, 7)
                          .map((d) => (
                            <span key={d} className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold" title={`Day ${d}`}>{d}</span>
                          ))}
                        {daysCompleted > 7 && (
                          <span className="inline-flex h-6 items-center rounded-md bg-emerald-500/15 text-emerald-600 text-[10px] font-medium px-1.5">+{daysCompleted - 7}</span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Journey timeline — moved up, with revisit */}
              {dashboard?.journeyPlans && dashboard.journeyPlans.length > 1 && (
                <JourneyTimeline
                  plans={(dashboard.journeyPlans as Array<{ planId: string; title: string; status: string; daysCompleted: number; totalDays: number; completionDate?: string; order: number }>)}
                  currentPlanId={plan?.id}
                  className=""
                  onRevisit={(planId) => {
                    const completedPlan = (dashboard?.journeyPlans ?? []).find(
                      (p: { planId: string }) => p.planId === planId
                    ) as { title?: string } | undefined;
                    const title = completedPlan?.title ?? 'completed plan';
                    sessionStorage.setItem(
                      COACH_START_LESSON_KEY,
                      JSON.stringify({
                        day: 0,
                        label: `Revisit: ${title}`,
                        prompt: `I want to revisit and review my completed learning plan "${title}". Can you help me recap the key lessons and takeaways?`,
                      })
                    );
                    navigate('/coach');
                  }}
                  onViewLesson={async (_planId, dayNumber) => {
                    // Fetch the lesson for this day from the user's lessons
                    try {
                      if (!email) return;
                      const lessons = await userLessonApi.getByPlanAndDay(email, _planId, dayNumber);
                      if (lessons.length > 0) {
                        handleLessonView(lessons[0]);
                      }
                    } catch (e) {
                      console.error('Failed to load lesson for revisit:', e);
                    }
                  }}
                />
              )}

              {/* Today's content — tips, nudges, tidbits, hacks, nanos (full content from template APIs) */}
              {(todayTipCount > 0 || todayNudgeCount > 0 || todayTidbitCount > 0 || todayHackCount > 0 || todayNanoCount > 0) && (
                <motion.div
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.18 }}
                >
                  <Card className="rounded-2xl border-2 overflow-hidden shadow-md bg-gradient-to-br from-primary/5 via-card to-violet-500/5 dark:from-violet-500/10">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        Today&apos;s content
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Tips, nudges & more — expand a day below to see full content
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {todayContentLoading ? (
                        <div className="flex justify-center py-8">
                          <BrandedLoading message="Loading today's content…" iconSize="sm" />
                        </div>
                      ) : (
                        <>
                          {todayContentItems.tips.filter(hasMeaningfulContent).map((t) => (
                            <TodayContentMiniCard
                              key={t.id ?? t.tipId ?? t.title}
                              title={(t as { title?: string }).title ?? ''}
                              body={getContentBody(t)}
                              variant="tip"
                              onOpenCoach={() => {
                                sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt: `Tell me more about: ${t.title}` }));
                                navigate('/coach');
                              }}
                            />
                          ))}
                          {todayContentItems.nudges.filter(hasMeaningfulContent).map((n) => (
                            <TodayContentMiniCard
                              key={n.id ?? n.nudgeId ?? n.title}
                              title={(n as { title?: string }).title ?? ''}
                              body={getContentBody(n)}
                              variant="nudge"
                              onOpenCoach={() => {
                                sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt: `Tell me more about: ${n.title}` }));
                                navigate('/coach');
                              }}
                            />
                          ))}
                          {todayContentItems.tidbits.filter(hasMeaningfulContent).map((tb) => (
                            <TodayContentMiniCard
                              key={tb.id ?? tb.tidbitId ?? tb.title}
                              title={(tb as { title?: string }).title ?? ''}
                              body={getContentBody(tb)}
                              variant="tidbit"
                              onOpenCoach={() => {
                                sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt: `Tell me more about: ${tb.title}` }));
                                navigate('/coach');
                              }}
                            />
                          ))}
                          {todayContentItems.hacks.filter(hasMeaningfulContent).map((h) => (
                            <TodayContentMiniCard
                              key={h.id ?? h.hackId ?? h.title}
                              title={(h as { title?: string }).title ?? ''}
                              body={getContentBody(h)}
                              variant="hack"
                              onOpenCoach={() => {
                                sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt: `Tell me more about: ${h.title}` }));
                                navigate('/coach');
                              }}
                            />
                          ))}
                          {todayContentItems.nanos.filter(hasMeaningfulContent).map((na) => (
                            <TodayContentMiniCard
                              key={na.id ?? na.title}
                              title={(na as { title?: string }).title ?? ''}
                              body={getContentBody(na)}
                              variant="nano"
                              onOpenCoach={() => {
                                sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt: `Tell me more about: ${na.title}` }));
                                navigate('/coach');
                              }}
                            />
                          ))}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Personalized Nudges from learning-goal-service */}
              {personalizedNudges.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.22 }}
                >
                  <Card className="rounded-2xl border-2 overflow-hidden shadow-md bg-gradient-to-br from-violet-500/5 via-card to-primary/5 dark:from-violet-500/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-primary" />
                        Today&apos;s Nudges
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {personalizedNudges.map((n, i) => {
                        const title = (n.title ?? '') as string;
                        const content = (n.content ?? n.nudgeText ?? n.nudge_text ?? n.description ?? '') as string;
                        const type = (n.nudgeType ?? n.nudge_type ?? n.category ?? '') as string;
                        return (
                          <div key={`pn-${i}`} className="rounded-lg border border-primary/15 bg-primary/5 dark:bg-primary/10 p-3">
                            <div className="flex items-start gap-2">
                              <MessageCircle className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                {title && <p className="text-xs font-semibold text-foreground">{title}</p>}
                                {content && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{content}</p>}
                                {type && <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>}
                              </div>
                            </div>
                            <button
                              className="text-[10px] mt-1.5 text-primary hover:text-primary/80 flex items-center gap-1"
                              onClick={() => {
                                sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt: `Tell me more about: ${title || content.slice(0, 60)}` }));
                                navigate('/coach');
                              }}
                            >
                              <MessageCircle className="w-3 h-3" /> Discuss with Coach
                            </button>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </aside>
          </div>

          {/* Your journey — win we're addressing (from API), pain points, skills we're addressing */}
          {(plan ?? learningPlanFromApi) && (() => {
            const { wins, painPoints, skills } = journeyContent;
            const rawPlanTitle = ((learningPlanFromApi ?? plan) as { title?: string })?.title ?? '';
            const planTitle = rawPlanTitle ? stripLearningJourneyPrefix(rawPlanTitle).trim() : '';
            const hasAny = wins.length > 0 || painPoints.length > 0 || skills.length > 0;
            const displayWin = primaryWinDetail ?? (wins[0] ? { name: wins[0] } : null);
            const otherWins = displayWin && wins.length > 1 ? wins.slice(1, 5) : [];

            return (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-12 pt-10 border-t border-border/60"
              >
                <div className="rounded-3xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-violet-500/5 dark:to-violet-500/10 overflow-hidden shadow-lg">
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-inner">
                        <Target className="h-6 w-6" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-foreground tracking-tight">Your journey</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {hasAny ? "The win we're addressing, pain points we're chasing, and skills we're building." : (planTitle && !isGenericLabel(planTitle) ? planTitle : "Complete your profile or talk to your Coach to personalize this.")}
                        </p>
                      </div>
                    </div>

                    {/* Primary Win card — from /wins API, prominent */}
                    {displayWin ? (
                      <div className="mb-8 rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent dark:from-emerald-500/20 dark:via-emerald-500/10 p-6 shadow-inner">
                        <div className="flex items-start gap-4">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 shadow-sm">
                            <Trophy className="h-7 w-7" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-xs font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1.5">
                              Win we&apos;re addressing
                            </h3>
                            <p className="text-lg font-semibold text-foreground leading-snug text-emerald-900 dark:text-emerald-100">
                              {displayWin.name}
                            </p>
                            {primaryWinDetail?.description && (
                              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                {primaryWinDetail.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-8 rounded-2xl border border-dashed border-emerald-500/25 bg-emerald-500/5 dark:bg-emerald-500/10 p-6">
                        <p className="text-sm text-muted-foreground">No active wins yet. Complete your profile or talk to your Coach to set your wins.</p>
                      </div>
                    )}

                    <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-8">
                      {otherWins.length > 0 && (
                        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 dark:bg-emerald-500/15 p-5">
                          <h3 className="text-xs font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-3 flex items-center gap-2">
                            <Trophy className="h-4 w-4" />
                            Other wins
                          </h3>
                          <ul className="space-y-2">
                            {otherWins.slice(0, 4).map((w, i) => (
                              <li key={i} className="text-sm text-foreground/90 leading-relaxed flex gap-2">
                                <span className="text-emerald-500 shrink-0">✓</span>
                                <span className="font-medium text-emerald-800 dark:text-emerald-200">{w}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {painPoints.length > 0 && (
                        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 dark:bg-amber-500/15 p-5">
                          <h3 className="text-xs font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                            <Circle className="h-4 w-4 fill-amber-500 text-amber-600 dark:text-amber-400" />
                            Pain points we&apos;re chasing
                          </h3>
                          <ul className="space-y-2">
                            {painPoints.slice(0, 4).map((p, i) => (
                              <li key={i} className="flex gap-2 text-sm text-foreground/90 leading-relaxed">
                                <span className="text-amber-500 dark:text-amber-400 shrink-0 mt-1.5 size-2 rounded-full bg-current" />
                                <span>{p}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="rounded-2xl border-2 border-violet-500/30 bg-gradient-to-br from-violet-500/15 via-violet-500/5 to-transparent dark:from-violet-500/20 dark:via-violet-500/10 p-5 shadow-inner">
                        <h3 className="text-xs font-semibold uppercase tracking-widest text-violet-700 dark:text-violet-300 mb-3 flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          Skills we&apos;re addressing
                        </h3>
                        {skills.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {skills.slice(0, 10).map((name: string, i: number) => (
                              <span
                                key={i}
                                className="rounded-xl bg-violet-500/25 px-3 py-1.5 text-sm font-medium text-violet-800 dark:text-violet-200 border border-violet-500/40 shadow-sm"
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            No skills specified for this plan yet. Add skills in your profile or they&apos;ll appear when your plan is updated.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Journey till date — progress so far and what this journey covers */}
                    <div className="mt-8 pt-8 border-t border-border/60">
                      <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Journey till date
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        <span className="font-semibold text-foreground tabular-nums">{daysCompleted}</span>
                        {' of '}
                        <span className="font-semibold text-foreground tabular-nums">{displayTotalDays}</span>
                        {' days completed'}
                        {daysCompleted >= displayTotalDays && displayTotalDays > 0 && ' — great progress!'}
                      </p>
                      {hasAny && (
                        <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6">
                          {wins.length > 0 && (
                            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2">Wins on this journey</h4>
                              <ul className="space-y-1.5 text-sm text-foreground/90">
                                {wins.slice(0, 4).map((w, i) => (
                                  <li key={i} className="flex gap-2">
                                    <span className="text-emerald-500 shrink-0">✓</span>
                                    <span>{w}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {painPoints.length > 0 && (
                            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-2">Pain points on this journey</h4>
                              <ul className="space-y-1.5 text-sm text-foreground/90">
                                {painPoints.slice(0, 4).map((p, i) => (
                                  <li key={i} className="flex gap-2">
                                    <span className="text-amber-500 dark:text-amber-400 shrink-0 mt-1.5 size-2 rounded-full bg-current" />
                                    <span>{p}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300 mb-2">Skills we&apos;re addressing</h4>
                            {skills.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {skills.slice(0, 8).map((name: string, i: number) => (
                                  <span
                                    key={i}
                                    className="rounded-md bg-violet-500/15 px-2 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300"
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground italic">None specified yet.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {!hasAny && (
                      <p className="text-sm text-muted-foreground italic mt-4">
                        Your wins, pain points, and skills will appear here as your plan is updated. Complete your profile or talk to your Coach to personalize this further.
                      </p>
                    )}
                  </div>
                </div>
              </motion.section>
            );
          })()}
        </div>
      </main>
      <Footer />

      <Dialog open={!!viewingLesson} onOpenChange={(open) => {
          if (!open) {
            setViewingLesson(null);
            setFullLesson(null);
          }
        }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl">
          {viewingLesson && (
            <>
              <DialogHeader>
                <DialogTitle>{viewingLesson.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {fullLesson?.content?.headline && (
                  <p className="text-lg font-semibold text-foreground border-l-4 border-primary pl-4">
                    {fullLesson.content.headline}
                  </p>
                )}
                {viewingLesson.description && !viewingLesson.content?.body && !(viewingLesson.content as Record<string, unknown>)?.text && !fullLesson?.content?.text && (
                  <p className="text-muted-foreground">{viewingLesson.description}</p>
                )}
                {(() => {
                  const contentRaw = viewingLesson.content as Record<string, unknown> | undefined;
                  const lessonRaw = viewingLesson as unknown as Record<string, unknown>;
                  const bodyText =
                    viewingLesson.content?.body ??
                    (typeof contentRaw?.text === 'string' ? contentRaw.text : null) ??
                    fullLesson?.content?.text ??
                    (typeof lessonRaw?.text === 'string' ? lessonRaw.text : null) ??
                    '';
                  const format = viewingLesson.content?.format ?? (fullLesson?.content?.text ? 'TEXT' : undefined);
                  if (bodyText) {
                    return (
                      <div className="rounded-xl border border-border bg-muted/20 p-4 sm:p-5">
                        <div className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed prose-pre:bg-transparent prose-pre:p-0 prose-pre:border-0">
                          {format === 'MARKDOWN' ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyText}</ReactMarkdown>
                          ) : format === 'HTML' ? (
                            <SanitizedHtml html={bodyText} className="prose prose-sm dark:prose-invert max-w-none" />
                          ) : (
                            <div className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                              {bodyText}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (viewingLesson.content?.sections && viewingLesson.content.sections.length > 0) {
                    return (
                      <div className="space-y-4">
                        {viewingLesson.content.sections.map((section, i) => (
                          <div key={i} className="rounded-xl border border-border bg-muted/20 p-4">
                            {section.title && <h4 className="font-semibold text-foreground mb-2">{section.title}</h4>}
                            {section.body && (
                              <div className="prose prose-sm dark:prose-invert max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.body}</ReactMarkdown>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  if (!bodyText && !viewingLesson.content?.sections?.length && viewingLesson.description) {
                    return (
                      <div className="rounded-xl border border-border bg-muted/20 p-4">
                        <p className="text-muted-foreground whitespace-pre-wrap">{viewingLesson.description}</p>
                      </div>
                    );
                  }
                  return <p className="text-sm text-muted-foreground italic">No content available for this lesson.</p>;
                })()}
                {(fullLesson?.followupPrompts?.length ?? viewingLesson?.followupPrompts?.length ?? 0) > 0 && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-primary" />
                      Ask your Coach
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">Click a prompt to open Coach with this question:</p>
                    <ul className="space-y-2">
                      {(fullLesson?.followupPrompts ?? viewingLesson?.followupPrompts ?? []).map((prompt, i) => (
                        <li key={i}>
                          <button
                            type="button"
                            onClick={() => {
                              sessionStorage.setItem(COACH_START_LESSON_KEY, JSON.stringify({ prompt }));
                              navigate('/coach');
                              setViewingLesson(null);
                              setFullLesson(null);
                            }}
                            className="text-left w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground hover:bg-accent hover:border-primary/30 transition-colors"
                          >
                            {prompt}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {viewingLesson.status !== 'COMPLETED' && viewingLesson.id && (
                  <div className="pt-4 flex gap-2">
                    <Button
                      size="sm"
                      className="rounded-lg"
                      onClick={async () => {
                        await handleLessonComplete(viewingLesson);
                        setViewingLesson(null);
                      }}
                      disabled={actionLoading === viewingLesson.id}
                    >
                      {actionLoading === viewingLesson.id ? (
                        <BrandedSpinner sizeClassName="h-4 w-4" />
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-1" />Mark complete</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
